import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import type { ExposureRow } from './exposure';
import type { HedgeBookEntry } from './hedge-book';

type DecimalString = string | null;

export interface BlPricingScheduleRow {
  id: number;
  blOrderName: string | null;
  loadedQuantityMt: DecimalString;
  totalQuantityMt: DecimalString;
  status: string | null;
  eta: string | null;
  ata: string | null;
  priceFixed: boolean;
  buyPriceFixed: boolean;
  sellPriceFixed: boolean;
}

export interface OrderDealContext {
  orderId: string;
  counterparty: string | null;
  physicalSide: 'Buy' | 'Sell' | null;
  pricing: string | null;
  commodity: string | null;
  contractQtyMt: string | null;
}

export interface OrderHedgingSummary {
  exposure: ExposureRow | null;
  blPricingSchedule: BlPricingScheduleRow[];
  requests: HedgeBookEntry[];
  dealContext: OrderDealContext | null;
  sidesPresent: { buy: boolean; sell: boolean };
  // Server-decided Hedge Side lock; null = user picks freely (all Inventory orders).
  directionLock: 'Buy' | 'Sell' | null;
  orderType: string;
}

export const ORDER_HEDGING_SUMMARY_QUERY_KEY = 'order:hedging-summary';

export function orderHedgingSummaryQueryKey(orderId: string): readonly [string, string] {
  return [ORDER_HEDGING_SUMMARY_QUERY_KEY, orderId];
}

export function fetchOrderHedgingSummary(orderId: string): Promise<OrderHedgingSummary> {
  return apiRequest<OrderHedgingSummary>(`/orders/${orderId}/hedging-summary`);
}

export function useOrderHedgingSummary(orderId: string | undefined) {
  return useQuery({
    queryKey: orderHedgingSummaryQueryKey(orderId ?? ''),
    queryFn: () => fetchOrderHedgingSummary(orderId ?? ''),
    enabled: typeof orderId === 'string' && orderId.length > 0,
  });
}
